/**
 * AppShell — shell autenticata per `/app/*`.
 *
 * Struttura responsive (un solo componente per tutti i breakpoint, vedi
 * rule responsive):
 *
 *   ┌──────────────────────────────────────────────────┐
 *   │ AppSidebar  │ AppTopBar (sticky)                 │
 *   │ (desktop)   ├──────────────────────────────────────┤
 *   │  hidden     │ main (Outlet)                       │
 *   │  md:flex    │   max-w-container-max-width         │
 *   │  w-60       │   padding gutter                    │
 *   │             │                                      │
 *   │             │ BottomTabBar (mobile) md:hidden     │
 *   └──────────────────────────────────────────────────┘
 *
 * Mantiene la logica di session timeout (PR2) già presente in
 * `AppLayout.tsx`, che questa shell sostituisce.
 */

import { useCallback, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../security/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import {
  SESSION_IDLE_MS,
  SESSION_WARNING_MS,
} from '../../security/constants';
import { PageTitleProvider } from '../../hooks/usePageTitle';
import { SessionTimeoutWarning } from './SessionTimeoutWarning';
import { AppSidebar } from './AppSidebar';
import { AppTopBar } from './AppTopBar';
import { BottomTabBar } from './BottomTabBar';
import { SetupRequiredBanner } from './SetupRequiredBanner';
import { TipologiaColoriProvider } from '../../pages/agenda/context/TipologiaColoriContext';

export interface AppShellProps {
  /**
   * Opt-in: da `lg:` in su rimuove il cap 1440px e i margini auto.
   * Default `false` = stringa di classi attuale, invariata.
   * In produzione lo attiva anche il path `/app/agenda` (BrowserRouter
   * non espone `useMatches` / `handle` di rotta).
   */
  fullWidth?: boolean;
}

function isAppShellFullWidthPath(pathname: string): boolean {
  return pathname === '/app/agenda' || pathname.startsWith('/app/agenda/');
}

/** Classi <main> di default — byte-identiche al layout pre-opt-in. */
const APP_SHELL_MAIN_CLASS =
  'flex-1 w-full max-w-container-max-width mx-auto px-margin-mobile md:px-margin-desktop py-6 md:py-gutter pb-24 md:pb-gutter';

/** Default + override `lg:` (max-w-none, mx-0, px-4 = 16px). */
const APP_SHELL_MAIN_CLASS_FULLWIDTH = `${APP_SHELL_MAIN_CLASS} lg:max-w-none lg:mx-0 lg:px-4`;

export function AppShell({ fullWidth = false }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const isFullWidth =
    fullWidth || isAppShellFullWidthPath(location.pathname);

  const onTimeout = useCallback(async () => {
    setWarningOpen(false);
    try {
      await signOut(auth);
    } finally {
      navigate('/login?reason=idle', { replace: true });
    }
  }, [navigate]);

  const { reset } = useSessionTimeout({
    enabled: !!user,
    onWarn: () => setWarningOpen(true),
    onTimeout,
  });

  const onStay = () => {
    setWarningOpen(false);
    reset();
  };

  return (
    <PageTitleProvider>
      <TipologiaColoriProvider>
      <div className="min-h-screen bg-bg-app flex">
        <AppSidebar />
        <div className="flex-1 min-w-0 flex flex-col">
          <SetupRequiredBanner />
          <AppTopBar />
          <main
            className={
              isFullWidth ? APP_SHELL_MAIN_CLASS_FULLWIDTH : APP_SHELL_MAIN_CLASS
            }
          >
            <Outlet />
          </main>
        </div>
        <BottomTabBar />
        <SessionTimeoutWarning
          isOpen={warningOpen}
          secondsRemaining={Math.floor(
            (SESSION_IDLE_MS - SESSION_WARNING_MS) / 1000
          )}
          onStay={onStay}
          onSignOutNow={() => void onTimeout()}
        />
      </div>
      </TipologiaColoriProvider>
    </PageTitleProvider>
  );
}
